import React, { useState, useEffect, useCallback } from "react";
import { Loader2, MailCheck, Trash2, SendHorizonal, KeyRound, Eye } from "lucide-react";
import { http } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_META = {
  connected: { label: "Connected", color: "#34d399" },
  error: { label: "Error", color: "#EF4444" },
  not_tested: { label: "Saved — not tested", color: "#EAB308" },
  not_configured: { label: "Not configured", color: "#9ca3af" },
};

function Panel({ title, children, testId, right }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid={testId}>
      <div className="mb-4 flex items-center justify-between"><h3 className="text-sm font-medium text-foreground">{title}</h3>{right}</div>
      {children}
    </div>
  );
}

export default function EmailSettingsTab() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ from_email: "", from_name: "" });
  const [keyInput, setKeyInput] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/email/settings");
      setS(data);
      setForm({ from_email: data.from_email || "", from_name: data.from_name || "" });
      setReplacing(!data.configured);
    } catch { toast.error("Failed to load email settings"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (keyInput.trim()) body.api_key = keyInput.trim();
      const { data } = await http.put("/email/settings", body);
      setS(data); setKeyInput(""); setReplacing(!data.configured);
      toast.success(body.api_key ? "API key saved (encrypted). Run a test to verify." : "Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  const removeKey = async () => {
    setBusy(true);
    try { const { data } = await http.delete("/email/settings/key"); setS(data); setReplacing(true); toast.success("API key removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/email/test", { to: testTo.trim() || undefined });
      toast.success(data.message || "Test email sent");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); await load(); }
    finally { setBusy(false); }
  };
  const loadPreview = async () => {
    try { const { data } = await http.get("/email/preview/password_reset"); setPreview(data); }
    catch { toast.error("Failed to load preview"); }
  };

  if (!s) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const st = STATUS_META[s.status] || STATUS_META.not_configured;

  return (
    <div className="max-w-3xl space-y-5">
      <Panel title="Resend email provider" testId="email-provider-panel"
        right={<span className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: `${st.color}1f`, color: st.color }} data-testid="email-status">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: st.color }} /> {st.label}
        </span>}>
        <p className="mb-4 text-xs text-muted-foreground">
          The platform sends transactional emails (password recovery codes) through Resend.
          The API key is stored encrypted on the backend and is never returned in full.
          {s.last_error && <span className="mt-1 block text-[#EF4444]" data-testid="email-last-error">Last error: {s.last_error}</span>}
        </p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Resend API Key</Label>
            {s.configured && !replacing ? (
              <div className="flex items-center gap-2">
                <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-2 font-mono text-sm text-foreground" data-testid="email-key-mask">
                  <KeyRound className="h-3.5 w-3.5 text-muted-foreground" /> {s.key_mask}
                </div>
                <Button variant="outline" size="sm" onClick={() => setReplacing(true)} data-testid="email-replace-key" className="border-white/15">Replace key</Button>
                <Button variant="outline" size="sm" onClick={removeKey} disabled={busy} data-testid="email-remove-key" className="border-white/15 text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /> Remove</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="re_..." data-testid="email-key-input"
                  className="border-white/[0.1] bg-[#0A0A0A] font-mono focus-visible:ring-white/20" autoComplete="off" />
                {s.configured && <Button variant="outline" size="sm" onClick={() => { setReplacing(false); setKeyInput(""); }} className="border-white/15">Cancel</Button>}
              </div>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">From email</Label>
              <Input value={form.from_email} onChange={(e) => setForm((f) => ({ ...f, from_email: e.target.value }))}
                placeholder="noreply@tracify.top" data-testid="email-from-email" className="border-white/[0.1] bg-[#0A0A0A] focus-visible:ring-white/20" />
              <p className="text-[11px] text-muted-foreground/70">Empty = Resend test sender (onboarding@resend.dev). Use your verified domain in production.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">From name</Label>
              <Input value={form.from_name} onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
                placeholder="Tracify" data-testid="email-from-name" className="border-white/[0.1] bg-[#0A0A0A] focus-visible:ring-white/20" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-4">
            <Button onClick={save} disabled={busy} data-testid="email-save" className="bg-white text-black hover:bg-white/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Test recipient (default: you)"
                data-testid="email-test-to" className="w-56 border-white/[0.1] bg-[#0A0A0A] text-sm focus-visible:ring-white/20" />
              <Button variant="outline" onClick={sendTest} disabled={busy || !s.configured} data-testid="email-send-test" className="gap-1.5 border-white/15">
                <SendHorizonal className="h-3.5 w-3.5" /> Send test
              </Button>
            </div>
          </div>
          {s.last_test_at && <p className="text-[11px] text-muted-foreground/70" data-testid="email-last-test">Last test: {fmtDateTime(s.last_test_at)}</p>}
        </div>
      </Panel>

      <Panel title="Password Reset template" testId="email-template-panel"
        right={<Button variant="outline" size="sm" onClick={loadPreview} data-testid="email-preview-btn" className="gap-1.5 border-white/15"><Eye className="h-3.5 w-3.5" /> Preview</Button>}>
        <p className="text-xs text-muted-foreground">
          <MailCheck className="mr-1.5 inline h-3.5 w-3.5" />
          Used for the 6-digit password recovery code. More templates (login alerts, payouts, welcome) can be added to the registry later.
        </p>
        {preview && (
          <iframe title="Password reset preview" srcDoc={preview} sandbox="" data-testid="email-preview-frame"
            className="mt-4 h-[420px] w-full rounded-lg border border-white/[0.08] bg-[#0a0a0a]" />
        )}
      </Panel>
    </div>
  );
}
